import { HttpEvent, HttpHandler, HttpInterceptor, HttpParams, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { AuthStore } from 'projects/shared/src/lib/@ngrx/auth/auth.reducer';
import { environment } from 'projects/shared/src/environments/environment';
import {
  buildLicenseScopePatch,
  deriveLoggedInLicenseScope,
  LicenseScopeRequestPatch,
  selectLicenseScopeFieldMapping,
} from 'projects/shared/src/lib/authentication/license-scope';
import {
  ILicenseScopeBodyTarget,
  ILicenseScopeEndpointRule,
  LICENSE_SCOPE_ENDPOINT_RULES,
  LicenseScopeApi,
} from 'projects/admin/src/app/license-scope/license-scope-endpoint-rules';

interface IRequestTarget {
  api: LicenseScopeApi;
  path: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

@Injectable()
export class LicenseScopeInterceptor implements HttpInterceptor {
  private readonly store = inject(Store);
  private readonly user = this.store.selectSignal(AuthStore.Selectors.user);
  private readonly userType = this.store.selectSignal(AuthStore.Selectors.userType);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const scope = deriveLoggedInLicenseScope(this.userType(), this.user());
    const target = this.resolveRequestTarget(req.url);
    if (!scope || !target) return next.handle(req);

    const rule: ILicenseScopeEndpointRule | undefined = LICENSE_SCOPE_ENDPOINT_RULES.find(
      (candidate) =>
        candidate.api === target.api &&
        candidate.method === req.method.toUpperCase() &&
        this.matchesEndpointPath(target.path, candidate.path),
    );
    if (!rule) return next.handle(req);

    const selectionOptions = {
      companyFieldsApplyToAllUserTypes:
        rule.api === 'workflow' || (rule.api === 'main' && rule.path.startsWith('/v1/users/')),
    };
    const queryMapping = selectLicenseScopeFieldMapping(rule.queryFields, scope.userType, selectionOptions);
    const supportedBodyFields = [
      ...rule.bodyFields,
      ...(rule.bodyTargets?.map((target) => target.field) ?? []),
    ];
    const bodyMapping = selectLicenseScopeFieldMapping(supportedBodyFields, scope.userType, selectionOptions);
    const params = queryMapping
      ? this.patchParams(req.params, buildLicenseScopePatch(scope, queryMapping))
      : req.params;
    const bodyPatch = bodyMapping ? buildLicenseScopePatch(scope, bodyMapping) : null;
    const shallowBodyPatch = bodyPatch ? this.selectSupportedPatch(bodyPatch, rule.bodyFields) : null;
    const shallowBody = shallowBodyPatch ? this.patchBody(req.body, shallowBodyPatch) : req.body;
    const body = bodyPatch
      ? (rule.bodyTargets ?? []).reduce(
          (value, target) => this.patchBodyTarget(value, bodyPatch, target),
          shallowBody,
        )
      : shallowBody;

    return next.handle(req.clone({ params, body }));
  }

  private patchParams(params: HttpParams, patch: LicenseScopeRequestPatch) {
    let scopedParams = params;
    for (const [field, value] of Object.entries(patch)) {
      if (value !== undefined) scopedParams = scopedParams.set(field, value);
    }
    return scopedParams;
  }

  private patchBody(body: unknown, patch: LicenseScopeRequestPatch): unknown {
    if (Array.isArray(body)) {
      return body.map((item) => (isRecord(item) ? { ...item, ...patch } : item));
    }
    return isRecord(body) ? { ...body, ...patch } : body;
  }

  private selectSupportedPatch(
    patch: LicenseScopeRequestPatch,
    supportedFields: ILicenseScopeEndpointRule['bodyFields'],
  ): LicenseScopeRequestPatch | null {
    const supportedPatch = supportedFields.reduce<LicenseScopeRequestPatch>((result, field) => {
      const value = patch[field];
      return value === undefined ? result : { ...result, [field]: value };
    }, {});

    return Object.keys(supportedPatch).length ? supportedPatch : null;
  }

  private patchBodyTarget(
    body: unknown,
    patch: LicenseScopeRequestPatch,
    target: ILicenseScopeBodyTarget,
  ): unknown {
    const value = patch[target.field];
    return value === undefined ? body : this.patchBodyPath(body, target.path, value);
  }

  private patchBodyPath(body: unknown, path: ILicenseScopeBodyTarget['path'], value: string): unknown {
    const [segment, ...remainingPath] = path;
    if (!segment) return body;

    if (segment === '[]') {
      return Array.isArray(body) ? body.map((item) => this.patchBodyPath(item, remainingPath, value)) : body;
    }
    if (!isRecord(body)) return body;
    if (remainingPath.length === 0) return { ...body, [segment]: value };

    return {
      ...body,
      [segment]: this.patchBodyPath(body[segment], remainingPath, value),
    };
  }

  private resolveRequestTarget(url: string): IRequestTarget | null {
    const apiBaseUrls: readonly [LicenseScopeApi, string | undefined][] = [
      ['gateway', environment.apiGatewayBaseUrl],
      ['main', environment.apiBaseUrl],
      ['workflow', environment.apiWorkflowBaseUrl],
    ];
    const urlWithoutQuery = url.split('?')[0];

    for (const [api, baseUrl] of apiBaseUrls) {
      if (!baseUrl) continue;
      const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
      if (!urlWithoutQuery.startsWith(normalizedBaseUrl)) continue;
      return {
        api,
        path: `/${urlWithoutQuery.slice(normalizedBaseUrl.length).replace(/^\/+/, '')}`,
      };
    }

    return null;
  }

  private matchesEndpointPath(actualPath: string, rulePath: ILicenseScopeEndpointRule['path']) {
    const actualSegments = actualPath.split('/').filter(Boolean);
    const ruleSegments = rulePath.split('/').filter(Boolean);
    if (actualSegments.length !== ruleSegments.length) return false;

    return ruleSegments.every(
      (segment, index) =>
        (segment.startsWith('{') && segment.endsWith('}')) || segment === actualSegments[index],
    );
  }
}
